import type { CSSProperties, MouseEvent, ReactNode, Ref } from 'react';

import { legacySpace } from '#/theme/theme';
import { getDataAndAriaProps } from '#/utils/getDataAndAriaProps';

import { StyledBox } from './Box.styled';

type Breakpoint = 'sm' | 'md' | 'lg' | 'xl';

type ResponsiveValue<T> =
  | T
  | null
  | readonly (T | null)[]
  | Partial<Record<Breakpoint, T | null>>;

type StyleProps = {
  position?: ResponsiveValue<string>;
  top?: ResponsiveValue<string | number>;
  right?: ResponsiveValue<string | number>;
  bottom?: ResponsiveValue<string | number>;
  left?: ResponsiveValue<string | number>;
  zIndex?: ResponsiveValue<string | number>;
  transform?: ResponsiveValue<string>;

  m?: ResponsiveValue<string | number>;
  margin?: ResponsiveValue<string | number>;
  mt?: ResponsiveValue<string | number>;
  marginTop?: ResponsiveValue<string | number>;
  mr?: ResponsiveValue<string | number>;
  marginRight?: ResponsiveValue<string | number>;
  mb?: ResponsiveValue<string | number>;
  marginBottom?: ResponsiveValue<string | number>;
  ml?: ResponsiveValue<string | number>;
  marginLeft?: ResponsiveValue<string | number>;
  mx?: ResponsiveValue<string | number>;
  my?: ResponsiveValue<string | number>;

  p?: ResponsiveValue<string | number>;
  padding?: ResponsiveValue<string | number>;
  pt?: ResponsiveValue<string | number>;
  paddingTop?: ResponsiveValue<string | number>;
  pr?: ResponsiveValue<string | number>;
  paddingRight?: ResponsiveValue<string | number>;
  pb?: ResponsiveValue<string | number>;
  paddingBottom?: ResponsiveValue<string | number>;
  pl?: ResponsiveValue<string | number>;
  paddingLeft?: ResponsiveValue<string | number>;
  px?: ResponsiveValue<string | number>;
  py?: ResponsiveValue<string | number>;

  display?: ResponsiveValue<string>;
  width?: ResponsiveValue<string | number>;
  height?: ResponsiveValue<string | number>;
  maxWidth?: ResponsiveValue<string | number>;
  maxHeight?: ResponsiveValue<string | number>;
  minWidth?: ResponsiveValue<string | number>;
  minHeight?: ResponsiveValue<string | number>;

  flex?: ResponsiveValue<string | number>;
  order?: ResponsiveValue<string | number>;
  alignSelf?: ResponsiveValue<string>;
  justifySelf?: ResponsiveValue<string>;
  alignItems?: ResponsiveValue<string>;
  alignContent?: ResponsiveValue<string>;
  justifyContent?: ResponsiveValue<string>;
  flexBasis?: ResponsiveValue<string | number>;
  flexDirection?: ResponsiveValue<string>;
  flexGrow?: ResponsiveValue<string | number>;
  flexShrink?: ResponsiveValue<string | number>;
  flexWrap?: ResponsiveValue<string>;

  columnStart?: ResponsiveValue<string>;
  columnEnd?: ResponsiveValue<string>;
  overflow?: ResponsiveValue<string>;

  color?: ResponsiveValue<string>;
  opacity?: ResponsiveValue<string | number>;
  visibility?: ResponsiveValue<string>;

  bg?: ResponsiveValue<string>;
  background?: ResponsiveValue<string>;
  backgroundImage?: ResponsiveValue<string>;
  backgroundSize?: ResponsiveValue<string>;
  backgroundPosition?: ResponsiveValue<string>;
  backgroundRepeat?: ResponsiveValue<string>;
  backgroundColor?: ResponsiveValue<string>;

  borderBottom?: ResponsiveValue<string>;
  borderTop?: ResponsiveValue<string>;
  borderLeft?: ResponsiveValue<string>;
  borderRight?: ResponsiveValue<string>;
  border?: ResponsiveValue<string>;
  borderColor?: ResponsiveValue<string>;
  borderWidth?: ResponsiveValue<string>;
  borderStyle?: ResponsiveValue<string>;
  borderRadius?: ResponsiveValue<string | number>;
  boxShadow?: ResponsiveValue<string>;

  cursor?: ResponsiveValue<string>;
  fontSize?: ResponsiveValue<string | number>;
  fontFamily?: ResponsiveValue<string>;
  lineHeight?: ResponsiveValue<string | number>;
  fontWeight?: ResponsiveValue<string | number>;
  letterSpacing?: ResponsiveValue<string | number>;
  textAlign?: ResponsiveValue<string>;
  verticalAlign?: ResponsiveValue<string>;
  textOverflow?: ResponsiveValue<string>;
  textTransform?: ResponsiveValue<string>;
  textDecoration?: ResponsiveValue<string>;
  whiteSpace?: ResponsiveValue<string>;
  wordBreak?: ResponsiveValue<string>;

  transition?: ResponsiveValue<string>;
  willChange?: ResponsiveValue<string>;
  pointerEvents?: ResponsiveValue<string>;

  gap?: ResponsiveValue<string | number>;
  gridGap?: ResponsiveValue<string | number>;
  gridAutoFlow?: ResponsiveValue<string>;
  gridArea?: ResponsiveValue<string>;
  gridTemplateAreas?: ResponsiveValue<string>;
  gridColumn?: ResponsiveValue<string>;
  gridColumnGap?: ResponsiveValue<string>;
  gridAutoColumns?: ResponsiveValue<string>;
  gridTemplateColumns?: ResponsiveValue<string>;
  gridRow?: ResponsiveValue<string>;
  gridRowGap?: ResponsiveValue<string>;
  gridAutoRows?: ResponsiveValue<string>;
  gridTemplateRows?: ResponsiveValue<string>;
};

/**
 * Long-form props are aliases for the short-form prop.
 *
 * The short name is always the canonical name.
 */
const ALIASES = {
  margin: 'm',
  marginTop: 'mt',
  marginRight: 'mr',
  marginBottom: 'mb',
  marginLeft: 'ml',

  padding: 'p',
  paddingTop: 'pt',
  paddingRight: 'pr',
  paddingBottom: 'pb',
  paddingLeft: 'pl',

  background: 'bg',

  gridGap: 'gap',
} as const;

type StyleProp = keyof StyleProps;
type CanonicalProp = StyleProp | (typeof ALIASES)[keyof typeof ALIASES];

const STYLE_PROPS = [
  'position',
  'top',
  'right',
  'bottom',
  'left',
  'zIndex',
  'transform',

  'm',
  'margin',
  'mt',
  'marginTop',
  'mr',
  'marginRight',
  'mb',
  'marginBottom',
  'ml',
  'marginLeft',
  'mx',
  'my',

  'p',
  'padding',
  'pt',
  'paddingTop',
  'pr',
  'paddingRight',
  'pb',
  'paddingBottom',
  'pl',
  'paddingLeft',
  'px',
  'py',

  'display',
  'width',
  'height',
  'maxWidth',
  'maxHeight',
  'minWidth',
  'minHeight',

  'flex',
  'order',
  'alignSelf',
  'justifySelf',
  'alignItems',
  'alignContent',
  'justifyContent',
  'flexBasis',
  'flexDirection',
  'flexGrow',
  'flexShrink',
  'flexWrap',

  'columnStart',
  'columnEnd',
  'overflow',

  'color',
  'opacity',
  'visibility',

  'bg',
  'background',
  'backgroundImage',
  'backgroundSize',
  'backgroundPosition',
  'backgroundRepeat',
  'backgroundColor',

  'borderBottom',
  'borderTop',
  'borderLeft',
  'borderRight',
  'border',
  'borderColor',
  'borderWidth',
  'borderStyle',
  'borderRadius',
  'boxShadow',

  'cursor',
  'fontSize',
  'fontFamily',
  'lineHeight',
  'fontWeight',
  'letterSpacing',
  'textAlign',
  'verticalAlign',
  'textOverflow',
  'textTransform',
  'textDecoration',
  'whiteSpace',
  'wordBreak',

  'transition',
  'willChange',
  'pointerEvents',

  'gridGap',
  'gridAutoFlow',
  'gridArea',
  'gridTemplateAreas',
  'gridColumn',
  'gridColumnGap',
  'gridAutoColumns',
  'gridTemplateColumns',
  'gridRow',
  'gridRowGap',
  'gridAutoRows',
  'gridTemplateRows',
] as const satisfies readonly StyleProp[];

/**
 * @deprecated We should remove this - it's not worth having something special like this
 * when we could simply use the actual value.
 *
 * It was added just for backwards compatibility with styled-system
 */
const SCALE_PROPERTIES = [
  'top',
  'right',
  'bottom',
  'left',

  'm',
  'mt',
  'mr',
  'mb',
  'ml',
  'mx',
  'my',

  'p',
  'pt',
  'pr',
  'pb',
  'pl',
  'px',
  'py',
] as const satisfies readonly StyleProp[];

const NUMERICAL_PIXEL_PROPERTIES = [
  'top',
  'right',
  'bottom',
  'left',

  'm',
  'margin',
  'mt',
  'marginTop',
  'mr',
  'marginRight',
  'mb',
  'marginBottom',
  'ml',
  'marginLeft',
  'mx',
  'my',

  'p',
  'padding',
  'pt',
  'paddingTop',
  'pr',
  'paddingRight',
  'pb',
  'paddingBottom',
  'pl',
  'paddingLeft',
  'px',
  'py',

  'width',
  'height',
  'maxWidth',
  'maxHeight',
  'minWidth',
  'minHeight',

  'flexBasis',

  'borderBottom',
  'borderTop',
  'borderLeft',
  'borderRight',
  'border',

  'borderWidth',
  'borderRadius',

  'fontSize',
  'letterSpacing',

  'gridGap',
  'gridColumnGap',
  'gridRowGap',
] as const satisfies readonly StyleProp[];

/**
 * @deprecated We should remove this - it's not worth having something special like this
 * when we could simply use the actual value.
 *
 * It was added just for backwards compatibility with styled-system
 */
const FRACTIONAL_PROPERTIES = [
  'width',
  'height',
  'minWidth',
  'maxWidth',
  'minHeight',
  'maxHeight',
] as const satisfies readonly StyleProp[];

function canonicalProp(prop: StyleProp): CanonicalProp {
  return ALIASES[prop as keyof typeof ALIASES] ?? prop;
}

function isResponsiveObject(
  value: unknown,
): value is Partial<Record<Breakpoint, unknown>> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function isResponsiveArray(value: unknown): value is readonly unknown[] {
  return Array.isArray(value);
}

function setVariable(
  variables: Record<string, string>,
  name: string,
  breakpoint: string | null,
  value: unknown,
) {
  if (value === null || typeof value === 'undefined') return;

  const breakpointSuffix = breakpoint ? `-${breakpoint}` : '';

  const computedValue = (() => {
    if (typeof value === 'number') {
      if (
        SCALE_PROPERTIES.includes(name) &&
        value > 0 &&
        value < legacySpace.length
      ) {
        return `${legacySpace[value]}px`;
      }

      if (FRACTIONAL_PROPERTIES.includes(name) && value > 0 && value <= 1) {
        return `${value * 100}%`;
      }

      if (NUMERICAL_PIXEL_PROPERTIES.includes(name)) {
        return `${value}px`;
      }
    }

    // oxlint-disable-next-line no-base-to-string
    return String(value);
  })();

  // oxlint-disable-next-line no-param-reassign
  variables[`--b-${name}${breakpointSuffix}`] = computedValue;
}

function setResponsiveVariables(
  variables: Record<string, string>,
  name: string,
  value: unknown,
) {
  if (value == null) return;

  if (isResponsiveArray(value)) {
    const [base, sm, md, lg, xl] = value;

    setVariable(variables, name, null, base);
    setVariable(variables, name, 'sm', sm);
    setVariable(variables, name, 'md', md);
    setVariable(variables, name, 'lg', lg);
    setVariable(variables, name, 'xl', xl);
  } else if (isResponsiveObject(value)) {
    for (const [breakpoint, breakpointValue] of Object.entries(value)) {
      setVariable(variables, name, breakpoint, breakpointValue);
    }
  } else {
    setVariable(variables, name, null, value);
  }
}

function getStyleVariables(props: StyleProps): Record<string, string> {
  const variables: Record<string, string> = {};

  for (const prop of STYLE_PROPS) {
    const value = props[prop];

    if (value == null) {
      continue;
    }

    const canonical = canonicalProp(prop);

    setResponsiveVariables(variables, canonical, value);
  }

  return variables;
}

type Props = StyleProps & {
  ref?: Ref<unknown>;
  id?: string;
  className?: string;
  children?: ReactNode;
  component?:
    | 'div'
    | 'span'
    | 'ul'
    | 'li'
    | 'nav'
    | 'strong'
    | 'em'
    | 'label'
    | 'main'
    | 'blockquote';
  onClick?: (event: MouseEvent<HTMLElement>) => void;
  qaId?: string;
  style?: CSSProperties;
};

export function Box({
  ref,
  id,
  className,
  children,
  component,
  onClick,
  qaId,
  style,
  ...otherProps
}: Props) {
  const styleVariables = getStyleVariables(otherProps);

  const dataAndAriaProps = getDataAndAriaProps(otherProps);

  return (
    <StyledBox
      as={component}
      // oxlint-disable-next-line react/jsx-props-no-spreading
      {...dataAndAriaProps}
      id={id}
      className={className}
      // oxlint-disable-next-line typescript/no-explicit-any
      ref={ref as Ref<any>}
      style={{ ...styleVariables, ...style }}
      onClick={onClick}
      data-qa-id={qaId || dataAndAriaProps['data-qa-id']}
    >
      {children}
    </StyledBox>
  );
}
